import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { analysisAPI, recentAnalysesAPI, subscriptionAPI, usageAPI, investmentCriteriaAPI } from '../api';
import { addGuestRecentAnalysis } from '../utils/guestRecentAnalyses';
import { refinanceCashOut, DSCR_INELIGIBLE_CONDITIONS, RENTAL_CONDITIONS } from '../utils/rentalMath';
import Header from '../components/Header';
import UsageLimitReached from '../components/UsageLimitReached';
import UsageWarningBanner from '../components/UsageWarningBanner';
import HardStopModal from '../components/HardStopModal';
import { Loader2, Link as LinkIcon, MapPin, DollarSign, AlertCircle, Info, Wrench, ChevronDown, Home, Hammer, Building2, ArrowRight, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { requiresSignupToAnalyze } from '../utils/sampleDeals';
import AuthPrompt from '../components/AuthPrompt';
import { FormattedCurrencyInput } from '../components/FormattedInput';
import { parseFormattedNumber } from '../utils/formatNumber';
import { DEFAULT_RENTAL_ORIGINATION_POINTS } from '../utils/financingDefaults';

const Analyzer = () => {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // BUG C (last hole): the deal must be judged against the criteria the user SAVED.
  // This screen kept its own thresholds — defaulting to 1.25 DSCR, or whatever a stale
  // saved form state carried — and passed those to /analyze. A user who saved a 1.0 DSCR
  // target was scored at 1.1/1.25 from the very first analysis, and no amount of fixing
  // the recalculate path could correct a number that was wrong on arrival.
  // Saved criteria win; a form value the user has since typed here is not overwritten.
  const criteriaSeededRef = useRef(false);
  useEffect(() => {
    if (!user || criteriaSeededRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await investmentCriteriaAPI.getCriteria();
        const rental = res?.data?.criteria?.rental;
        if (cancelled || !rental) return;
        criteriaSeededRef.current = true;
        // `??` so a deliberate 0 threshold survives.
        if (rental.min_monthly_cash_flow !== undefined && rental.min_monthly_cash_flow !== null)
          setRentalMinCashFlow(rental.min_monthly_cash_flow);
        if (rental.min_dscr !== undefined && rental.min_dscr !== null)
          setRentalMinDscr(rental.min_dscr);
        if (rental.min_cap_rate !== undefined && rental.min_cap_rate !== null)
          setRentalMinCapRate(rental.min_cap_rate);
        if (rental.max_price_vs_market_pct !== undefined && rental.max_price_vs_market_pct !== null)
          setRentalMaxPriceVsMarket(rental.max_price_vs_market_pct);
      } catch (e) {
        // Non-fatal: fall back to the on-screen defaults rather than blocking analysis.
        console.warn('[Analyzer] could not load saved investment criteria', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);
  
  const [inputValue, setInputValue] = useState('');
  const [inputType, setInputType] = useState('address');
  const [askingPrice, setAskingPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPriceInput, setShowPriceInput] = useState(false);
  const [propertyPreview, setPropertyPreview] = useState(null);
  
  // Rehab condition state (for Fix & Flip)
  const [rehabCondition, setRehabCondition] = useState('auto');
  const [customRehabPerSqft, setCustomRehabPerSqft] = useState('');
  const [customRehabTotal, setCustomRehabTotal] = useState('');
  const [customRehabInputMode, setCustomRehabInputMode] = useState('total'); // 'total' or 'persqft'
  
  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [strategy, setStrategy] = useState('flip'); // flip, rental, brrrr
  // No module asks for the subject's condition any more, so this is a constant, not state.
  //
  // Fix&flip never used it: analyze_deal forces 'average' for flip/brrrr, and all five
  // options returned the identical ARV on every property measured. Rentals already ask
  // for condition on the scale that drives DSCR eligibility, and answering this one too
  // let a user declare 'turnkey' and 'distressed' about the same house at once. In
  // valuation only 'distressed' moved the number at all, by an amount that ranged from
  // -0.8% to -36.5% and is validated against nothing; that mode returns when there is
  // data to calibrate it.
  //
  // Sent EXPLICITLY rather than left out: server.py's `or 'average'` stays as a defence,
  // but the value comes from here.
  const propertyCondition = 'average';
  const autocompleteRef = useRef(null);
  const debounceRef = useRef(null);
  
  // Rental-specific state
  const [rentalPropertyCondition, setRentalPropertyCondition] = useState('turnkey'); // For DSCR eligibility
  const [rentalFinancingType, setRentalFinancingType] = useState('dscr'); // cash or dscr
  const [rentalPurchaseType, setRentalPurchaseType] = useState('new_purchase'); // 'new_purchase' or 'refinancing'
  const [rentalDownPaymentPct, setRentalDownPaymentPct] = useState(25);
  const [rentalInterestRate, setRentalInterestRate] = useState(7.5);
  
  // Multi-unit rent inputs (CRITICAL: user-defined rent is mandatory)
  const [userRentInputs, setUserRentInputs] = useState(['']); // Array for multi-unit
  const [suggestedRent, setSuggestedRent] = useState(0); // From comps/RentCast
  const [propertyTypeForRental, setPropertyTypeForRental] = useState('sfr'); // sfr, duplex, triplex, fourplex
  
  // Get unit count from property type
  const getUnitCount = (type) => {
    switch(type) {
      case 'duplex': return 2;
      case 'triplex': return 3;
      case 'fourplex': return 4;
      default: return 1; // sfr
    }
  };
  const unitCount = getUnitCount(propertyTypeForRental);
  
  // User-defined rental criteria
  const [rentalMinCashFlow, setRentalMinCashFlow] = useState(200); // $/month
  const [rentalMinDscr, setRentalMinDscr] = useState(1.25);
  const [rentalMinCapRate, setRentalMinCapRate] = useState(6); // %
  const [rentalMaxPriceVsMarket, setRentalMaxPriceVsMarket] = useState(100); // % of market value
  const [rentalToleranceMode, setRentalToleranceMode] = useState('balanced'); // strict, balanced, aggressive, custom
  
  // Tolerance Presets - Auto-fill criteria based on mode
  const TOLERANCE_PRESETS = {
    strict: {
      minCashFlow: 200,
      minDscr: 1.25,
      minCapRate: 7,
      maxPriceVsMarket: 95
    },
    balanced: {
      minCashFlow: 0,
      minDscr: 1.1,
      minCapRate: 6,
      maxPriceVsMarket: 100
    },
    aggressive: {
      minCashFlow: -200,
      minDscr: 1.0,
      minCapRate: 5,
      maxPriceVsMarket: 110
    }
  };
  
  // Auto-fill criteria when tolerance mode changes (except custom)
  useEffect(() => {
    if (rentalToleranceMode !== 'custom' && TOLERANCE_PRESETS[rentalToleranceMode]) {
      const preset = TOLERANCE_PRESETS[rentalToleranceMode];
      setRentalMinCashFlow(preset.minCashFlow);
      setRentalMinDscr(preset.minDscr);
      setRentalMinCapRate(preset.minCapRate);
      setRentalMaxPriceVsMarket(preset.maxPriceVsMarket);
    }
  }, [rentalToleranceMode]);
  
  // Annual costs state (shared for flip and rental)
  const [annualPropertyTaxes, setAnnualPropertyTaxes] = useState('');
  const [annualInsurance, setAnnualInsurance] = useState('');
  const [annualUtilities, setAnnualUtilities] = useState('');
  
  // Rental-specific closing costs and fees (PHASE 1: Complete Financial Model)
  const [rentalClosingCostsPct, setRentalClosingCostsPct] = useState(3); // % of purchase price
  const [rentalOriginationPoints, setRentalOriginationPoints] = useState(DEFAULT_RENTAL_ORIGINATION_POINTS); // % of loan
  const [rentalLenderFees, setRentalLenderFees] = useState(''); // Flat dollar
  const [rentalEscrowReserves, setRentalEscrowReserves] = useState(''); // Flat dollar
  const [rentalTitleAttorneyFees, setRentalTitleAttorneyFees] = useState(''); // Flat dollar
  const [rentalOtherClosingCosts, setRentalOtherClosingCosts] = useState(''); // Flat dollar (editable)
  const [rentalSellerAgentCommission, setRentalSellerAgentCommission] = useState(0); // % (optional, can be 0)
  
  // Refinancing-specific state
  const [appraisedValue, setAppraisedValue] = useState('');
  const [existingPayoff, setExistingPayoff] = useState('0');
  const [refiLTV, setRefiLTV] = useState(75);
  
  // Prevent duplicate requests
  const analysisInProgress = useRef(false);
  
  // Sample deal loading state
  const [loadingSampleDeal, setLoadingSampleDeal] = useState(null); // 'flip' or 'rental'
  
  // Usage limit state
  const [showSignupGate, setShowSignupGate] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  
  // Usage warning and hard stop state
  const [usageWarning, setUsageWarning] = useState(null);
  const [hardStopData, setHardStopData] = useState(null);
  const [showHardStopModal, setShowHardStopModal] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  
  // DSCR eligibility. Mirrors DSCR_INELIGIBLE_CONDITIONS in rental_engine.py (and the
  // same rule in rentalMath.js). This used to be `=== 'turnkey'`, which is STRICTER than
  // the engine: it would have denied DSCR on light and moderate condition properties that
  // the engine — and real lenders — allow. It never fired only because nothing could
  // change the condition.
  const isRentalDscrEligible = !DSCR_INELIGIBLE_CONDITIONS.includes(rentalPropertyCondition);

  // Fall back to cash when the condition rules DSCR out. The button is also disabled and
  // the reason is shown, so this is a backstop for a condition changed after the fact —
  // not a silent rewrite of a choice the user can still see selected.
  useEffect(() => {
    if (rentalFinancingType === 'dscr' && !isRentalDscrEligible) {
      setRentalFinancingType('cash');
    }
  }, [rentalPropertyCondition, rentalFinancingType, isRentalDscrEligible]);
  
  // Get total user rent (sum of all units)
  const totalUserRent = userRentInputs.reduce((sum, rent) => sum + (parseFloat(rent) || 0), 0);
  
  // Update rent inputs array when property type changes
  useEffect(() => {
    const newUnitCount = getUnitCount(propertyTypeForRental);
    setUserRentInputs(prev => {
      if (prev.length !== newUnitCount) {
        // Preserve existing values and fill new ones with empty string
        return Array(newUnitCount).fill('').map((_, i) => 
          i < prev.length ? prev[i] : ''
        );
      }
      return prev;
    });
  }, [propertyTypeForRental]);
  
  // SESSION STORAGE: Save form state for navigation recovery
  useEffect(() => {
    // Debounce the save to avoid excessive writes
    const timeoutId = setTimeout(() => {
      if (inputValue || askingPrice || strategy !== 'flip') {
        const formState = {
          inputValue,
          inputType,
          askingPrice,
          showPriceInput,
          strategy,
          rehabCondition,
          customRehabPerSqft,
          // Rental-specific
          rentalPropertyCondition,
          rentalFinancingType,
          rentalDownPaymentPct,
          rentalInterestRate,
          propertyTypeForRental,
          userRentInputs,
          rentalMinCashFlow,
          rentalMinDscr,
          rentalMinCapRate,
          rentalMaxPriceVsMarket,
          rentalToleranceMode,
          // Annual costs
          annualPropertyTaxes,
          annualInsurance,
          annualUtilities,
          // Rental closing costs & fees
          rentalClosingCostsPct,
          rentalOriginationPoints,
          rentalLenderFees,
          rentalEscrowReserves,
          rentalTitleAttorneyFees,
          rentalOtherClosingCosts,
          rentalSellerAgentCommission,
          timestamp: Date.now()
        };
        try {
          sessionStorage.setItem('chekdeal_analyzer_form', JSON.stringify(formState));
        } catch (e) {
          console.warn('Failed to save form state:', e);
        }
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [inputValue, askingPrice, strategy, rehabCondition, 
      rentalPropertyCondition, rentalFinancingType, rentalDownPaymentPct, 
      rentalInterestRate, propertyTypeForRental, userRentInputs,
      rentalMinCashFlow, rentalMinDscr, rentalMinCapRate, rentalMaxPriceVsMarket,
      rentalToleranceMode, annualPropertyTaxes, annualInsurance, annualUtilities,
      rentalClosingCostsPct, rentalOriginationPoints, rentalLenderFees,
      rentalEscrowReserves, rentalTitleAttorneyFees, rentalOtherClosingCosts,
      rentalSellerAgentCommission,
      inputType, showPriceInput, customRehabPerSqft]);
  
  // SESSION STORAGE: Restore form state on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('chekdeal_analyzer_form');
      if (saved) {
        const formState = JSON.parse(saved);
        // Only restore if saved less than 30 minutes ago
        if (formState.timestamp && Date.now() - formState.timestamp < 30 * 60 * 1000) {
          console.log('[Analyzer] Restoring form state from sessionStorage');
          setInputType(formState.inputType || 'address');
          setAskingPrice('');
          setShowPriceInput(false);
          setStrategy(formState.strategy || 'flip');
          setRehabCondition(formState.rehabCondition || 'auto');
          setCustomRehabPerSqft(formState.customRehabPerSqft || '');
          // Rental-specific
          setRentalPropertyCondition(formState.rentalPropertyCondition || 'turnkey');
          setRentalFinancingType(formState.rentalFinancingType || 'dscr');
          setRentalDownPaymentPct(formState.rentalDownPaymentPct || 25);
          setRentalInterestRate(formState.rentalInterestRate || 7.5);
          setPropertyTypeForRental(formState.propertyTypeForRental || 'sfr');
          if (formState.userRentInputs && formState.userRentInputs.some(r => parseFloat(r) > 0)) {
            setUserRentInputs(formState.userRentInputs);
          }
          setRentalMinCashFlow(formState.rentalMinCashFlow || 200);
          setRentalMinDscr(formState.rentalMinDscr || 1.25);
          setRentalMinCapRate(formState.rentalMinCapRate || 6);
          setRentalMaxPriceVsMarket(formState.rentalMaxPriceVsMarket || 100);
          setRentalToleranceMode(formState.rentalToleranceMode || 'balanced');
          // Annual costs
          setAnnualPropertyTaxes(formState.annualPropertyTaxes || '');
          setAnnualInsurance(formState.annualInsurance || '');
          setAnnualUtilities(formState.annualUtilities || '');
          // Rental closing costs & fees
          if (formState.rentalClosingCostsPct !== undefined) setRentalClosingCostsPct(formState.rentalClosingCostsPct);
          if (formState.rentalOriginationPoints !== undefined) setRentalOriginationPoints(formState.rentalOriginationPoints);
          if (formState.rentalLenderFees) setRentalLenderFees(formState.rentalLenderFees);
          if (formState.rentalEscrowReserves) setRentalEscrowReserves(formState.rentalEscrowReserves);
          if (formState.rentalTitleAttorneyFees) setRentalTitleAttorneyFees(formState.rentalTitleAttorneyFees);
          if (formState.rentalOtherClosingCosts) setRentalOtherClosingCosts(formState.rentalOtherClosingCosts);
          if (formState.rentalSellerAgentCommission !== undefined) setRentalSellerAgentCommission(formState.rentalSellerAgentCommission);
        }
      }
    } catch (e) {
      console.warn('Failed to restore form state:', e);
    }
  }, []); // Run only on mount
  
  // Fetch usage status for warning banners
  useEffect(() => {
    const fetchUsageStatus = async () => {
      if (!user?.user_id || user.user_id === 'guest') return;
      
      try {
        const response = await usageAPI.getStatus(user.user_id);
        const data = response.data;
        
        if (data.warning_level && !data.blocked) {
          setUsageWarning({
            level: data.warning_level,
            costRatioPct: data.cost_ratio_pct,
            thresholdPct: data.threshold_pct,
            planType: data.plan_type,
            message: data.message
          });
        } else {
          setUsageWarning(null);
        }
      } catch (error) {
        console.warn('Failed to fetch usage status:', error);
      }
    };
    
    fetchUsageStatus();
  }, [user?.user_id]);

  // Detect if input is a listing URL
  const isListingURL = (value) => {
    return value.includes('zillow.com') || 
           value.includes('redfin.com') || 
           value.includes('realtor.com');
  };

  // Fetch autocomplete suggestions
  const fetchSuggestions = async (query) => {
    // Require at least 5 characters to reduce API calls
    if (query.length < 5 || isListingURL(query)) {
      setSuggestions([]);
      return;
    }
    
    setAutocompleteLoading(true);
    try {
      const response = await analysisAPI.autocomplete(query);
      setSuggestions(response.data.suggestions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setAutocompleteLoading(false);
    }
  };

  // Handle input change with auto-detection and autocomplete
  const handleInputChange = (value) => {
    setInputValue(value);
    
    // Auto-detect URL vs address
    if (isListingURL(value)) {
      setInputType('url');
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      setInputType('address');
      
      // Debounce autocomplete requests (600ms to reduce API calls)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 600);
    }
    
    // Show price input after address/URL is entered
    if (value.trim().length > 10) {
      setShowPriceInput(true);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    // Debug logging for verification
    console.log('=== AUTOCOMPLETE SELECTION DEBUG ===');
    console.log('Raw suggestion object:', suggestion);
    console.log('full_address field:', suggestion.full_address);
    console.log('address field:', suggestion.address);
    
    // Use the full_address which now includes the street
    const selectedAddress = suggestion.full_address || suggestion.address;
    console.log('Value written to input:', selectedAddress);
    
    setInputValue(selectedAddress);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowPriceInput(true);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Validate before analysis
  const canAnalyze = () => {
    if (!inputValue.trim()) return false;
    
    // For manual addresses, asking price is highly recommended
    if (inputType === 'address' && !askingPrice) {
      return true; // Allow but will show warning
    }
    
    return true;
  };

  const handleAnalyze = async () => {
    if (!inputValue.trim()) {
      toast.error(lang === 'en' ? 'Please enter a property address or URL' : 'Ingresa una dirección o URL');
      return;
    }
    
    // Prevent duplicate requests
    if (analysisInProgress.current || loading) {
      console.log('[Analyzer] Request already in progress, ignoring duplicate click');
      return;
    }

    // Warn if no asking price for manual address (skip for Refinancing and Valuation paths)
    if (inputType === 'address' && !askingPrice && 
        !(strategy === 'rental' && rentalPurchaseType === 'refinancing') &&
        strategy !== 'valuation') {
      const confirmWithoutPrice = window.confirm(
        lang === 'en' 
          ? 'No asking price entered. The analysis will use a default value ($65,000) which may not reflect accurate profit projections.\n\nContinue anyway?'
          : 'Sin precio de venta. El análisis usará un valor predeterminado ($65,000) que puede no reflejar proyecciones precisas.\n\n¿Continuar de todos modos?'
      );
      if (!confirmWithoutPrice) return;
    }

    // SIGNUP GATE: analyzing your OWN address requires an account. The two sample
    // deals stay open so a visitor can see real output first. Without this every
    // logged-out visitor was user_id 'guest' and shared ONE exhausted 3-analysis
    // bucket, so new arrivals hit the upgrade wall on their first action.
    if (requiresSignupToAnalyze(user, inputValue)) {
      setShowSignupGate(true);
      return;
    }

    analysisInProgress.current = true;
    setLoading(true);
    
    try {
      // NEW TWO-STEP FLOW: First fetch property details WITHOUT running analysis
      const inputTypeForAPI = isListingURL(inputValue) ? 'url' : 'address';
      
      // Step 1: Fetch property details only
      console.log('[Analyzer] Calling fetchProperty API...', inputTypeForAPI, inputValue.trim());
      const propertyResponse = await analysisAPI.fetchProperty(inputTypeForAPI, inputValue.trim());
      console.log('[Analyzer] fetchProperty response:', propertyResponse.data);
      
      if (propertyResponse.data?.success) {
        // Navigate to confirmation page with property data
        console.log('[Analyzer] Success! Navigating to /confirm...');
        navigate('/confirm', { 
          state: { 
            property: propertyResponse.data.property,
            askingPrice: (strategy === 'rental' && rentalPurchaseType === 'refinancing')
              ? (appraisedValue ? parseFloat(appraisedValue) : 0)
              : strategy === 'valuation'
              ? (askingPrice ? parseFloat(askingPrice) : 0)
              : (askingPrice ? parseFloat(askingPrice) : propertyResponse.data.property.listing_price),
            strategy: strategy,
            propertyCondition: propertyCondition,
            rehabCondition: rehabCondition,
            customRehabTotal: customRehabTotal,
            customRehabPerSqft: customRehabPerSqft,
            customRehabInputMode: customRehabInputMode,
            targetRule: 70,  // Default MAO rule
            lang: lang,
            // For rental mode, pass rental settings
            rentalPropertyCondition: rentalPropertyCondition,
            rentalFinancingType: rentalFinancingType,
            totalUserRent: totalUserRent,
            userRentInputs: userRentInputs,
            unitCount: unitCount,
            propertyTypeForRental: propertyTypeForRental,
            // New rental fields
            rentalPurchaseType: rentalPurchaseType,
            appraisedValue: appraisedValue,
            existingPayoff: existingPayoff,
            refiLTV: refiLTV,
            annualPropertyTaxes: annualPropertyTaxes,
            annualInsurance: annualInsurance,
            rentalDownPaymentPct: rentalDownPaymentPct,
            rentalInterestRate: rentalInterestRate,
            rentalMinCashFlow: rentalMinCashFlow,
            rentalMinDscr: rentalMinDscr,
            rentalMinCapRate: rentalMinCapRate,
            rentalToleranceMode: rentalToleranceMode,
            rentalClosingCostsPct: rentalClosingCostsPct,
            rentalOriginationPoints: rentalOriginationPoints,
            rentalLenderFees: rentalLenderFees,
            rentalTitleAttorneyFees: rentalTitleAttorneyFees,
          }
        });
      } else {
        throw new Error(propertyResponse.data?.message || 'Failed to fetch property details');
      }
      
    } catch (error) {
      console.error('Property fetch error:', error);
      
      // Handle common errors
      if (error.response?.status === 404) {
        toast.error(lang === 'en' 
          ? 'Property not found. Please verify the address.'
          : 'Propiedad no encontrada. Por favor verifica la dirección.');
        return;
      }
      
      // Check for 429 Too Many Requests (hard stop - cost limit reached)
      if (error.response?.status === 429) {
        const detail = error.response.data?.detail;
        if (detail?.error === 'USAGE_LIMIT_REACHED') {
          setHardStopData(detail);
          setShowHardStopModal(true);
          toast.error(detail?.message || 'You\'ve reached your usage limit');
          return;
        }
      }
      
      // Check for network errors (no response)
      if (!error.response) {
        toast.error(lang === 'en' 
          ? 'Network error. Please check your connection and try again.'
          : 'Error de red. Por favor verifica tu conexión e intenta de nuevo.');
        return;
      }
      
      // Extract error message
      let message;
      if (error.response?.data?.detail) {
        message = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : error.response.data.detail.message || 'Failed to fetch property';
      } else {
        message = lang === 'en' ? 'Failed to fetch property details' : 'Error al obtener detalles de propiedad';
      }
      
      toast.error(message);
    } finally {
      analysisInProgress.current = false;
      setLoading(false);
    }
  };
  
  // Legacy direct analysis (used by sample deals)
  const handleDirectAnalyze = async (requestData) => {
    setLoading(true);
    try {
      const response = await analysisAPI.analyze(requestData);
      
      // Auto-save to recent history (non-blocking)
      try {
        if (user?.user_id) {
          await recentAnalysesAPI.addRecent({
            user_id: user.user_id,
            analysis_data: response.data,
            strategy: requestData.strategy || 'flip',
            arv_mode: null
          });
        } else {
          addGuestRecentAnalysis(response.data, requestData.strategy || 'flip', null);
        }
      } catch (recentError) {
        console.warn('Failed to save to recent history:', recentError);
      }
      
      // V5.0: Clear stale session data and navigate with explicit analysis_id
      sessionStorage.removeItem('chekdeal_last_analysis');
      sessionStorage.removeItem('chekdeal_fresh_analysis_id');
      const newAnalysisId = response.data.analysis_id || response.data.id;
      
      // Dispatch usage-updated event to refresh header counter
      try {
        window.dispatchEvent(new Event('usage-updated'));
      } catch (e) {
        console.warn('Failed to dispatch usage-updated event:', e);
      }
      
      navigate(`/results/${newAnalysisId}`, { 
        state: { analysis: response.data },
        replace: true 
      });
      
    } catch (error) {
      console.error('Analysis error:', error);
      
      if (error.response?.status === 429) {
        const detail = error.response.data?.detail;
        if (detail?.error === 'USAGE_LIMIT_REACHED') {
          setHardStopData(detail);
          setShowHardStopModal(true);
          return;
        }
      }
      
      if (error.response?.status === 402) {
        const detail = error.response.data?.detail;
        setUsageInfo(detail?.usage || null);
        setLimitReached(true);
        return;
      }
      
      toast.error(getApiErrorMessage(error, 'Analysis failed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle sample deal analysis - automatically runs full analysis
  // THE TWO EXAMPLES, SERVED FROZEN. This used to build a request and call
  // analysisAPI.analyze — the same POST /api/analyze as a paid analysis — so opening an
  // example hit RentCast on the owner's key, was counted against a signed-in user's plan
  // (the gate exempted samples; the record did not), and was saved as one of the user's
  // own analyses. Now it is a file read on the server: GET /api/sample-deals/{kind}.
  // Nothing is written anywhere — not recent history, not usage — and the result opens
  // on /results/view marked is_sample, where every action that would recompute, edit or
  // persist it is blocked.
  const handleSampleDealAnalysis = async (dealType) => {
    if (loadingSampleDeal || analysisInProgress.current) return;
    if (dealType !== 'flip' && dealType !== 'rental') return;
    analysisInProgress.current = true;
    setLoadingSampleDeal(dealType);
    try {
      const response = await analysisAPI.getSampleDeal(dealType, lang);
      const sample = { ...response.data, is_sample: true };
      sessionStorage.removeItem('chekdeal_last_analysis');
      sessionStorage.removeItem('chekdeal_fresh_analysis_id');
      navigate(`/results/sample-${dealType}`, { state: { analysis: sample }, replace: true });
    } catch (error) {
      console.error('[SampleDeal] Could not load the example:', error);
      toast.error(lang === 'en' ? 'Could not load the example. Try again.' : 'No se pudo cargar el ejemplo. Intenta de nuevo.');
    } finally {
      analysisInProgress.current = false;
      setLoadingSampleDeal(null);
    }
  };

  // Format price input
  const formatPrice = (value) => {
    // Remove non-numeric characters
    const numeric = value.replace(/[^0-9]/g, '');
    if (!numeric) return '';
    
    // Format as currency with commas
    const number = parseInt(numeric, 10);
    return number.toLocaleString('en-US');
  };

  // Format display price with dollar sign
  const formatDisplayPrice = (value) => {
    if (!value) return '';
    const formatted = formatPrice(value);
    return formatted ? `$${formatted}` : '';
  };

  // Get raw numeric value from formatted string
  const getRawPrice = (formattedValue) => {
    return formattedValue.replace(/[^0-9]/g, '');
  };

  // Handle price input change with live formatting
  const handlePriceChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    setAskingPrice(rawValue);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Limit Reached State */}
      {/* SIGNUP GATE — shown when a logged-out visitor tries their OWN address.
          One-click Google sign-up; the sample deals below stay open without it. */}
      {showSignupGate && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
          data-testid="signup-gate"
          onClick={() => setShowSignupGate(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
            <AuthPrompt
              icon={UserPlus}
              title={lang === 'en'
                ? 'Create a free account to analyze this property'
                : 'Crea una cuenta gratis para analizar esta propiedad'}
              description={lang === 'en'
                ? 'Free account includes 3 full analyses — no card required. Or try a sample deal below without signing up.'
                : 'La cuenta gratis incluye 3 análisis completos — sin tarjeta. O prueba un ejemplo abajo sin registrarte.'}
              lang={lang}
              redirectAfterAuth="/analyze"
            />
            <button
              type="button"
              onClick={() => setShowSignupGate(false)}
              className="mt-3 w-full text-sm text-white/80 hover:text-white min-h-[44px] touch-manipulation"
              data-testid="signup-gate-dismiss"
            >
              {lang === 'en' ? 'Not now — show me a sample deal' : 'Ahora no — ver un ejemplo'}
            </button>
          </div>
        </div>
      )}

      {limitReached && (
        <UsageLimitReached
          usage={usageInfo}
          onViewPricing={() => navigate('/pricing')}
          onViewHistory={() => navigate('/deals')}
          lang={lang}
        />
      )}
      
      {/* Hard Stop Modal */}
      <HardStopModal
        isOpen={showHardStopModal}
        onClose={() => setShowHardStopModal(false)}
        errorData={hardStopData}
        onViewDeals={() => navigate('/deals')}
        onGoToPricing={() => navigate('/pricing')}
      />

      {/* Main Content - Only show when limit not reached */}
      {!limitReached && (
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto">
          
          {/* Usage Warning Banner */}
          {usageWarning && !warningDismissed && (
            <UsageWarningBanner
              warningLevel={usageWarning.level}
              costRatioPct={usageWarning.costRatioPct}
              thresholdPct={usageWarning.thresholdPct}
              planType={usageWarning.planType}
              onDismiss={() => setWarningDismissed(true)}
            />
          )}
          
          {/* Title */}
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-primary mb-4">
              {t('hero_subtitle')}
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground px-4">
              {t('hero_description')}
            </p>
          </div>

          {/* Input Type Tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => { setInputType('address'); setInputValue(''); setShowPriceInput(false); }}
              className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-all text-sm sm:text-base ${
                inputType === 'address'
                  ? 'bg-secondary text-white shadow-sm'
                  : 'bg-white text-slate-600 border border-border hover:bg-slate-50'
              }`}
              data-testid="tab-address"
            >
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">{t('input_address_label')}</span>
              <span className="sm:hidden">{lang === 'en' ? 'Address' : 'Dirección'}</span>
            </button>
            <button
              onClick={() => { setInputType('url'); setInputValue(''); setShowPriceInput(false); }}
              className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-all text-sm sm:text-base ${
                inputType === 'url'
                  ? 'bg-secondary text-white shadow-sm'
                  : 'bg-white text-slate-600 border border-border hover:bg-slate-50'
              }`}
              data-testid="tab-url"
            >
              <LinkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">{t('input_url_label')}</span>
              <span className="sm:hidden">URL</span>
            </button>
          </div>

          {/* Main Input Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-border p-6 sm:p-8">
            <div className="space-y-5">
              {/* Property Address/URL Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {inputType === 'address' ? t('input_address_label') : t('input_url_label')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative" ref={autocompleteRef}>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && showPriceInput && handleAnalyze()}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    onTouchStart={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder={inputType === 'address' 
                      ? '123 Main St, Detroit, MI 48226'
                      : 'https://www.zillow.com/homedetails/...'}
                    className="w-full px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-border rounded-lg focus:outline-none focus:border-secondary transition-colors"
                    disabled={loading}
                    data-testid="property-input"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="text"
                  />
                  
                  {/* Autocomplete Loading Indicator */}
                  {autocompleteLoading && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                    </div>
                  )}
                  
                  {/* Autocomplete Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && inputType === 'address' && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="autocomplete-dropdown">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSelectSuggestion(suggestion)}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            handleSelectSuggestion(suggestion);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-slate-50 active:bg-slate-100 flex items-start gap-3 border-b border-border last:border-b-0 transition-colors touch-manipulation"
                          data-testid={`suggestion-item-${index}`}
                        >
                          <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-medium text-primary text-sm">
                              {suggestion.address || suggestion.full_address}
                            </div>
                            {suggestion.city && (
                              <div className="text-xs text-muted-foreground">
                                {suggestion.city}, {suggestion.state} {suggestion.zip_code}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Asking Price Input - Shows after address is entered (hidden for Refinancing and Valuation) */}
              {showPriceInput && !(strategy === 'rental' && rentalPurchaseType === 'refinancing') && strategy !== 'valuation' && (
                <div className="animate-in slide-in-from-top-2 duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-sm font-medium text-slate-700">
                      {lang === 'en' ? 'Asking Price' : 'Precio de Venta'}
                      {inputType === 'address' && (
                        <span className="text-amber-600 ml-1 font-normal">
                          ({lang === 'en' ? 'recommended' : 'recomendado'})
                        </span>
                      )}
                    </label>
                    <div className="group relative">
                      <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                      <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all w-64 z-10">
                        {lang === 'en' 
                          ? 'The asking price directly affects: Net Profit calculation, Maximum Allowable Offer (MAO), and Deal Score. Enter the actual listing price for accurate analysis.'
                          : 'El precio de venta afecta directamente: Cálculo de ganancia neta, Oferta máxima permitida (MAO), y Puntuación del trato.'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-slate-600 font-medium">$</span>
                    <input
                      type="text"
                      value={askingPrice ? parseInt(askingPrice, 10).toLocaleString('en-US') : ''}
                      onChange={handlePriceChange}
                      onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
                      placeholder={lang === 'en' ? 'e.g., 150,000' : 'ej., 150,000'}
                      className="w-full pl-10 pr-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-border rounded-lg focus:outline-none focus:border-secondary transition-colors"
                      disabled={loading}
                      data-testid="asking-price-input"
                    />
                  </div>
                  
                  {/* Price impact notice */}
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      {lang === 'en' 
                        ? 'Asking price strongly affects net profit, MAO comparison, and deal score. For accurate analysis, enter the actual listing price.'
                        : 'El precio de venta afecta significativamente la ganancia neta, comparación MAO y puntuación del trato. Para un análisis preciso, ingresa el precio real.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Strategy Selector - Segmented Button Group */}
              {showPriceInput && (
                <div className="mt-4 sm:mt-6 animate-fade-in" data-testid="strategy-selector-section">
                  <div className="flex items-center gap-2 mb-3">
                    <label className="text-sm font-medium text-primary">
                      {lang === 'en' ? 'Analysis Type' : 'Tipo de Análisis'}
                    </label>
                  </div>
                  
                  <div className="inline-flex flex-wrap rounded-lg border-2 border-border bg-white p-1 gap-1">
                    {[
                      { value: 'flip', label: 'Fix & Flip', labelEs: 'Compra/Venta', icon: '🔨' },
                      { value: 'rental', label: 'Rental', labelEs: 'Alquiler', icon: '🏠' },
                      { value: 'brrrr', label: 'BRRRR', labelEs: 'BRRRR', icon: '🔄' },
                      { value: 'valuation', label: 'Value / Comps', labelEs: 'Valor / Comps', icon: '📊' },
                    ].filter(option => option.value !== 'brrrr').map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setStrategy(option.value)}
                        className={`px-3 sm:px-4 py-2.5 rounded-md text-sm font-semibold transition-all ${
                          strategy === option.value
                            ? 'bg-secondary text-white shadow-sm'
                            : 'text-muted-foreground hover:text-primary hover:bg-slate-50'
                        }`}
                        data-testid={`strategy-${option.value}`}
                      >
                        <span className="mr-1.5">{option.icon}</span>
                        {lang === 'es' ? option.labelEs : option.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Strategy description */}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {strategy === 'flip' && (lang === 'en' 
                      ? 'ARV weighted toward recently renovated comps for post-repair value estimation.'
                      : 'ARV ponderado hacia comparables renovados para estimar valor post-reparación.')}
                    {strategy === 'rental' && (lang === 'en'
                      ? 'Balanced ARV with emphasis on rental income metrics (cap rate, cashflow).'
                      : 'ARV balanceado con énfasis en métricas de renta (tasa de capitalización, flujo de efectivo).')}
                    {strategy === 'brrrr' && (lang === 'en'
                      ? 'Hybrid approach: considers both post-repair value and rental potential for refinancing.'
                      : 'Enfoque híbrido: considera valor post-reparación y potencial de renta para refinanciamiento.')}
                    {strategy === 'valuation' && (lang === 'en'
                      ? 'Property value estimation only - comparable sales, ARV, and market data without investment metrics.'
                      : 'Solo estimación de valor - ventas comparables, ARV y datos de mercado sin métricas de inversión.')}
                  </p>
                </div>
              )}

              {/* REHAB BUDGET — the Auto/Light/Medium/Heavy presets were removed on a
                  lender's recommendation: a $/sqft band is not an investment-grade number.
                  The investor's own budget is the only path now.

                  UI only. `rehabCondition` stays 'auto' and is still carried forward, so a
                  blank budget still yields the auto-detected estimate — reading blank as $0
                  would make every deal look profitable. */}
              {showPriceInput && strategy === 'flip' && (
                <div className="mt-4 sm:mt-6 animate-fade-in" data-testid="rehab-condition-section">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-sm font-medium text-primary">
                      <Wrench className="inline w-4 h-4 mr-1" />
                      {lang === 'en' ? 'Rehab Budget' : 'Presupuesto de Remodelación'}
                    </label>
                    <div className="group relative">
                      <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                      <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all w-64 z-10">
                        {lang === 'en'
                          ? 'Enter your real renovation budget. Left blank, it is estimated from the property age and price.'
                          : 'Ingresa tu presupuesto real de renovación. En blanco, se estima por la edad y el precio de la propiedad.'}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 animate-fade-in space-y-3">
                      {/* Input Mode Toggle */}
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="analyzer-rehab-mode"
                            checked={customRehabInputMode === 'total'}
                            onChange={() => setCustomRehabInputMode('total')}
                            className="w-4 h-4 text-secondary"
                            data-testid="analyzer-rehab-mode-total"
                          />
                          <span className={customRehabInputMode === 'total' ? 'font-medium text-foreground text-sm' : 'text-muted-foreground text-sm'}>
                            {lang === 'en' ? 'Total Budget' : 'Presupuesto Total'}
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="analyzer-rehab-mode"
                            checked={customRehabInputMode === 'persqft'}
                            onChange={() => setCustomRehabInputMode('persqft')}
                            className="w-4 h-4 text-secondary"
                            data-testid="analyzer-rehab-mode-persqft"
                          />
                          <span className={customRehabInputMode === 'persqft' ? 'font-medium text-foreground text-sm' : 'text-muted-foreground text-sm'}>
                            {lang === 'en' ? '$/sqft' : '$/pie²'}
                          </span>
                        </label>
                      </div>
                      
                      {/* Total Budget Input */}
                      {customRehabInputMode === 'total' && (
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">
                            {lang === 'en' ? 'Total Rehab Budget' : 'Presupuesto Total de Remodelación'}
                          </label>
                          <div className="max-w-[200px]">
                            {/* The same control PropertyConfirmation already uses for
                                this exact field. The raw <input> that was here only
                                stripped non-digits, so a budget typed as 30000 stayed
                                "30000" on this screen while the identical field one
                                step later read "$30,000".

                                Stored formatted, as everywhere else: the consumers all
                                strip $ and commas before arithmetic (PropertyConfirmation
                                handleRunAnalysis, rerunRequest.toNumber), so the value
                                that reaches the backend is still a plain number. */}
                            <FormattedCurrencyInput
                              value={customRehabTotal}
                              onChange={(val) => setCustomRehabTotal(val)}
                              prefix="$"
                              placeholder="e.g., 50,000"
                              className="w-full pr-4 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm"
                              data-testid="analyzer-custom-rehab-total"
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Per SqFt Input */}
                      {customRehabInputMode === 'persqft' && (
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">
                            {lang === 'en' ? 'Rehab Cost per SqFt' : 'Costo por Pie²'}
                          </label>
                          <div className="relative max-w-[200px]">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                              type="number"
                              value={customRehabPerSqft}
                              onChange={(e) => setCustomRehabPerSqft(e.target.value)}
                              placeholder="e.g., 35"
                              className="w-full pl-8 pr-16 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary text-sm"
                              data-testid="analyzer-custom-rehab-persqft"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">/sqft</span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}


              {/* RENTAL-SPECIFIC INPUTS - Redesigned with Path Selector */}
              {showPriceInput && strategy === 'rental' && (
                <div className="mt-4 sm:mt-6 space-y-5 animate-fade-in">
                  
                  {/* PATH SELECTOR */}
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <label className="text-sm font-semibold text-blue-800 block mb-3">
                      {lang === 'en' ? 'Rental Strategy' : 'Estrategia de Renta'}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setRentalPurchaseType('new_purchase')}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          rentalPurchaseType === 'new_purchase'
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : 'border-blue-200 bg-white text-blue-700 hover:border-blue-400'
                        }`}
                        data-testid="rental-path-new-purchase"
                      >
                        🛒 {lang === 'en' ? 'New Purchase' : 'Compra Nueva'}
                        <div className="text-xs opacity-75 mt-1">
                          {lang === 'en' ? 'Buy to rent' : 'Comprar para rentar'}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRentalPurchaseType('refinancing');
                          setRentalFinancingType('dscr'); // Always DSCR for refi
                        }}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          rentalPurchaseType === 'refinancing'
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : 'border-blue-200 bg-white text-blue-700 hover:border-blue-400'
                        }`}
                        data-testid="rental-path-refinancing"
                      >
                        🔄 {lang === 'en' ? 'Refinancing' : 'Refinanciamiento'}
                        <div className="text-xs opacity-75 mt-1">
                          {lang === 'en' ? 'Cash-out DSCR refi' : 'Refinanciar con cash-out'}
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* PROPERTY CONDITION — drives DSCR eligibility.
                      This was never asked. The state existed and was plumbed all the way
                      through to the backend, but nothing could change it, so every rental
                      was analysed as "turnkey" and eligibility was never actually
                      evaluated: a gutted property was told it qualified for a DSCR loan no
                      lender would write. Placed above the path split because eligibility
                      applies to a refinance loan just as much as to a purchase loan. */}
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <label className="text-sm font-semibold text-blue-800 block mb-1">
                      {lang === 'en' ? 'Condition for Financing' : 'Condición para el financiamiento'}
                    </label>
                    <p className="text-xs text-blue-700/80 mb-3">
                      {lang === 'en'
                        ? 'Lenders will not write a DSCR loan on a property that is not rent-ready.'
                        : 'Los prestamistas no otorgan un préstamo DSCR sobre una propiedad que no está lista para rentar.'}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {RENTAL_CONDITIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setRentalPropertyCondition(opt.value)}
                          className={`px-2 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            rentalPropertyCondition === opt.value
                              ? 'border-blue-500 bg-blue-500 text-white'
                              : 'border-blue-200 bg-white text-blue-700 hover:border-blue-400'
                          }`}
                          data-testid={`rental-condition-${opt.value}`}
                        >
                          <div>{lang === 'en' ? opt.en : opt.es}</div>
                          <div className="text-xs opacity-75">
                            {DSCR_INELIGIBLE_CONDITIONS.includes(opt.value)
                              ? (lang === 'en' ? 'cash only' : 'solo efectivo')
                              : (lang === 'en' ? 'DSCR ok' : 'DSCR ok')}
                          </div>
                        </button>
                      ))}
                    </div>
                    {!isRentalDscrEligible && (
                      <div
                        className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2"
                        data-testid="dscr-ineligible-reason"
                      >
                        {lang === 'en'
                          ? 'Not eligible for DSCR financing in this condition — it must be repaired first. This deal will be analyzed as a cash purchase.'
                          : 'No califica para financiamiento DSCR en esta condición — debe repararse primero. Este trato se analizará como compra en efectivo.'}
                      </div>
                    )}
                  </div>

                  {/* PATH A: REFINANCING */}
                  {rentalPurchaseType === 'refinancing' && (
                    <div className="space-y-4">
                      
                      {/* Appraised Value */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          🏦 {lang === 'en' ? 'Appraised Value (ARV)' : 'Valor de Appraisal (ARV)'}
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                          <FormattedCurrencyInput
                            value={appraisedValue}
                            onChange={(val) => setAppraisedValue(parseFormattedNumber(val) ?? '')}
                            prefix={null}
                            placeholder="247,500"
                            className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                            data-testid="refi-appraised-value"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {lang === 'en' ? 'Official appraised value from lender' : 'Valor oficial del appraisal del prestamista'}
                        </p>
                      </div>

                      {/* Existing Payoff */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Existing Mortgage Payoff' : 'Saldo Hipoteca Existente'}
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                          <FormattedCurrencyInput
                            value={existingPayoff}
                            onChange={(val) => setExistingPayoff(parseFormattedNumber(val) ?? '')}
                            prefix={null}
                            placeholder="0"
                            className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                            data-testid="refi-existing-payoff"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {lang === 'en' ? 'Enter 0 if property is free and clear' : 'Pon 0 si la propiedad no tiene deuda'}
                        </p>
                      </div>

                      {/* LTV % */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Loan-to-Value (LTV)' : 'Relación Préstamo-Valor (LTV)'}
                        </label>
                        <div className="flex gap-2 mb-2">
                          {[65, 70, 75, 80].map((ltv) => (
                            <button
                              key={ltv}
                              type="button"
                              onClick={() => setRefiLTV(ltv)}
                              className={`flex-1 py-1.5 text-sm rounded-lg border-2 font-medium ${
                                refiLTV === ltv
                                  ? 'border-blue-500 bg-blue-500 text-white'
                                  : 'border-slate-200 text-slate-600 hover:border-blue-300'
                              }`}
                              data-testid={`refi-ltv-${ltv}`}
                            >
                              {ltv}%
                            </button>
                          ))}
                        </div>
                        {appraisedValue && (
                          <p className="text-xs text-blue-600 font-medium">
                            {lang === 'en' ? 'Loan Amount:' : 'Monto del Préstamo:'} 
                            ${Math.round(parseFloat(appraisedValue) * refiLTV / 100).toLocaleString()}
                          </p>
                        )}
                      </div>

                      {/* Interest Rate */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Interest Rate' : 'Tasa de Interés'}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={rentalInterestRate}
                            onChange={(e) => setRentalInterestRate(parseFloat(e.target.value))}
                            step="0.125"
                            min="0"
                            max="20"
                            className="w-full pr-8 pl-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                            data-testid="refi-interest-rate"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                        </div>
                      </div>

                      {/* Origination Points */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Origination Points' : 'Puntos de Originación'}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={rentalOriginationPoints}
                            onChange={(e) => setRentalOriginationPoints(parseFloat(e.target.value))}
                            step="0.5"
                            min="0"
                            max="10"
                            className="w-full pr-8 pl-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                            data-testid="refi-origination-points"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                        </div>
                      </div>

                      {/* Lender Fees */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Lender Fees' : 'Costos del Prestamista'}
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Lender/Underwriting $' : 'Prestamista/Underwriting $'}
                            </label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <FormattedCurrencyInput
                                value={rentalLenderFees}
                                onChange={(val) => setRentalLenderFees(parseFormattedNumber(val) ?? '')}
                                prefix={null}
                                placeholder="0"
                                className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                data-testid="refi-lender-fees"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Title/Attorney $' : 'Título/Abogado $'}
                            </label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <FormattedCurrencyInput
                                value={rentalTitleAttorneyFees}
                                onChange={(val) => setRentalTitleAttorneyFees(parseFormattedNumber(val) ?? '')}
                                prefix={null}
                                placeholder="0"
                                className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                data-testid="refi-title-attorney"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Property Type */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Property Type' : 'Tipo de Propiedad'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            {value: 'sfr', label: lang === 'en' ? 'Single Family' : 'Unifamiliar', units: 1, icon: '🏠'},
                            {value: 'duplex', label: 'Duplex', units: 2, icon: '🏘️'},
                            {value: 'triplex', label: 'Triplex', units: 3, icon: '🏢'},
                            {value: 'fourplex', label: '4-Plex', units: 4, icon: '🏗️'},
                          ].map((type) => (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => setPropertyTypeForRental(type.value)}
                              className={`p-2 rounded-lg border-2 text-xs font-medium ${
                                propertyTypeForRental === type.value
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-slate-200 text-slate-600'
                              }`}
                              data-testid={`refi-property-type-${type.value}`}
                            >
                              {type.icon} {type.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Monthly Rent Per Unit */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          💰 {lang === 'en' ? 'Monthly Rent (Per Unit)' : 'Renta Mensual (Por Unidad)'}
                        </label>
                        {userRentInputs.map((rent, index) => (
                          <div key={index} className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-slate-500 w-14">
                              {lang === 'en' ? `Unit ${index + 1}:` : `Unidad ${index + 1}:`}
                            </span>
                            <div className="relative flex-1">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <FormattedCurrencyInput
                                value={rent}
                                onChange={(val) => {
                                  const newInputs = [...userRentInputs];
                                  // store the PARSED number: consumers parseFloat() this, and
                                  // parseFloat("1,400") would be 1.
                                  newInputs[index] = parseFormattedNumber(val) ?? '';
                                  setUserRentInputs(newInputs);
                                }}
                                prefix={null}
                                placeholder="0"
                                className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                data-testid={`refi-rent-input-${index}`}
                              />
                            </div>
                            <span className="text-xs text-slate-400">/mo</span>
                          </div>
                        ))}
                        <div className="text-sm font-semibold text-blue-700 mt-2">
                          {lang === 'en' ? 'Total:' : 'Total:'} 
                          ${userRentInputs.reduce((s, r) => s + (parseFloat(r) || 0), 0).toLocaleString()}/mo
                        </div>
                      </div>

                      {/* Annual Expenses */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Annual Expenses' : 'Gastos Anuales'}
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Property Taxes /yr' : 'Impuestos /año'}
                            </label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <FormattedCurrencyInput
                                value={annualPropertyTaxes}
                                onChange={(val) => setAnnualPropertyTaxes(parseFormattedNumber(val) ?? '')}
                                prefix={null}
                                placeholder="1,800"
                                className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                data-testid="refi-annual-taxes"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Insurance /yr' : 'Seguro /año'}
                            </label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <FormattedCurrencyInput
                                value={annualInsurance}
                                onChange={(val) => setAnnualInsurance(parseFormattedNumber(val) ?? '')}
                                prefix={null}
                                placeholder="1,600"
                                className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                data-testid="refi-annual-insurance"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Cash Out Preview */}
                      {appraisedValue && (
                        <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                          <label className="text-sm font-semibold text-green-800 block mb-2">
                            💵 {lang === 'en' ? 'Estimated Cash Out' : 'Cash Out Estimado'}
                          </label>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-green-700">
                                {lang === 'en' ? 'Loan Amount:' : 'Monto del Préstamo:'}
                              </span>
                              <span className="font-semibold text-green-800">
                                ${Math.round(parseFloat(appraisedValue || 0) * refiLTV / 100).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-green-700">
                                {lang === 'en' ? 'Less Payoff:' : 'Menos Saldo:'}
                              </span>
                              <span className="font-semibold text-red-600">
                                -${Math.round(parseFloat(existingPayoff || 0)).toLocaleString()}
                              </span>
                            </div>
                            {/* Refinancing is not free. The preview used to stop at
                                loan - payoff, so it promised more cash than the analysis
                                could deliver ($97,902 here vs a real $91,025). Both screens
                                now run the SAME shared formula. */}
                            <div className="flex justify-between">
                              <span className="text-green-700">
                                {lang === 'en' ? 'Less Closing Costs:' : 'Menos Costos de Cierre:'}
                              </span>
                              <span className="font-semibold text-red-600">
                                -${Math.round(refinanceCashOut({
                                  appraisedValue, ltvPct: refiLTV, existingPayoff,
                                  closingCostsPct: rentalClosingCostsPct,
                                  originationPoints: rentalOriginationPoints,
                                }).closing).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-green-700">
                                {lang === 'en' ? 'Less Origination:' : 'Menos Originación:'}
                              </span>
                              <span className="font-semibold text-red-600">
                                -${Math.round(refinanceCashOut({
                                  appraisedValue, ltvPct: refiLTV, existingPayoff,
                                  closingCostsPct: rentalClosingCostsPct,
                                  originationPoints: rentalOriginationPoints,
                                }).origination).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between border-t border-green-200 pt-1">
                              <span className="font-bold text-green-800">
                                {lang === 'en' ? 'Estimated Cash Out:' : 'Cash Out Estimado:'}
                              </span>
                              <span className="font-bold text-green-700 text-base"
                                    data-testid="input-estimated-cash-out">
                                ${Math.round(refinanceCashOut({
                                  appraisedValue, ltvPct: refiLTV, existingPayoff,
                                  closingCostsPct: rentalClosingCostsPct,
                                  originationPoints: rentalOriginationPoints,
                                }).cashOut).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  )}

                  {/* PATH B: NEW PURCHASE */}
                  {rentalPurchaseType === 'new_purchase' && (
                    <div className="space-y-4">

                      {/* Financing Type */}
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                        <label className="text-sm font-semibold text-blue-800 block mb-3">
                          {lang === 'en' ? 'Financing Type' : 'Tipo de Financiamiento'}
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          {/* Disabled when the condition rules DSCR out, rather than
                              accepting the click and silently reverting it a moment later.
                              A control that appears to work and then undoes itself is
                              worse than one that plainly cannot be used. */}
                          <button
                            type="button"
                            disabled={!isRentalDscrEligible}
                            title={!isRentalDscrEligible
                              ? (lang === 'en'
                                  ? 'Not available in this property condition — repairs are required first.'
                                  : 'No disponible en esta condición — se requieren reparaciones primero.')
                              : undefined}
                            onClick={() => setRentalFinancingType('dscr')}
                            className={`p-3 rounded-lg border-2 text-sm font-medium ${
                              !isRentalDscrEligible
                                ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                : rentalFinancingType === 'dscr'
                                  ? 'border-blue-500 bg-blue-500 text-white'
                                  : 'border-blue-200 bg-white text-blue-700'
                            }`}
                            data-testid="new-purchase-financing-dscr"
                          >
                            🏦 DSCR Loan
                            <div className="text-xs opacity-75 mt-1">
                              {!isRentalDscrEligible
                                ? (lang === 'en' ? 'Not eligible' : 'No califica')
                                : (lang === 'en' ? 'Rent-ready property' : 'Propiedad lista')}
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setRentalFinancingType('cash')}
                            className={`p-3 rounded-lg border-2 text-sm font-medium ${
                              rentalFinancingType === 'cash'
                                ? 'border-blue-500 bg-blue-500 text-white'
                                : 'border-blue-200 bg-white text-blue-700'
                            }`}
                            data-testid="new-purchase-financing-cash"
                          >
                            💵 {lang === 'en' ? 'Cash Purchase' : 'Compra en Efectivo'}
                            <div className="text-xs opacity-75 mt-1">
                              {lang === 'en' ? 'Any condition' : 'Cualquier condición'}
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Down Payment - DSCR only */}
                      {rentalFinancingType === 'dscr' && (
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <label className="text-sm font-semibold text-slate-700 block mb-2">
                            {lang === 'en' ? 'Down Payment' : 'Enganche'}
                          </label>
                          <div className="flex gap-2 mb-2">
                            {[0, 20, 25, 30].map((pct) => (
                              <button
                                key={pct}
                                type="button"
                                onClick={() => setRentalDownPaymentPct(pct)}
                                className={`flex-1 py-1.5 text-sm rounded-lg border-2 font-medium ${
                                  rentalDownPaymentPct === pct
                                    ? 'border-blue-500 bg-blue-500 text-white'
                                    : 'border-slate-200 text-slate-600'
                                }`}
                                data-testid={`new-purchase-down-payment-${pct}`}
                              >
                                {pct}%
                              </button>
                            ))}
                          </div>
                          <div className="relative">
                            <input
                              type="number"
                              value={rentalDownPaymentPct}
                              onChange={(e) => setRentalDownPaymentPct(parseFloat(e.target.value) || 0)}
                              min={0}
                              max={100}
                              step={5}
                              className="w-full pr-8 pl-3 py-2 border border-slate-200 rounded-lg text-sm"
                              data-testid="new-purchase-down-payment-input"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                          </div>
                        </div>
                      )}

                      {/* Interest Rate - DSCR only */}
                      {rentalFinancingType === 'dscr' && (
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <label className="text-sm font-semibold text-slate-700 block mb-2">
                            {lang === 'en' ? 'Interest Rate' : 'Tasa de Interés'}
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={rentalInterestRate}
                              onChange={(e) => setRentalInterestRate(parseFloat(e.target.value))}
                              step="0.125"
                              min="0"
                              max="20"
                              className="w-full pr-8 pl-3 py-2 border border-slate-200 rounded-lg text-sm"
                              data-testid="new-purchase-interest-rate"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                          </div>
                        </div>
                      )}

                      {/* Property Type */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Property Type' : 'Tipo de Propiedad'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            {value: 'sfr', label: lang === 'en' ? 'Single Family' : 'Unifamiliar', icon: '🏠'},
                            {value: 'duplex', label: 'Duplex', icon: '🏘️'},
                            {value: 'triplex', label: 'Triplex', icon: '🏢'},
                            {value: 'fourplex', label: '4-Plex', icon: '🏗️'},
                          ].map((type) => (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => setPropertyTypeForRental(type.value)}
                              className={`p-2 rounded-lg border-2 text-xs font-medium ${
                                propertyTypeForRental === type.value
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-slate-200 text-slate-600'
                              }`}
                              data-testid={`new-purchase-property-type-${type.value}`}
                            >
                              {type.icon} {type.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Monthly Rent Per Unit */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          💰 {lang === 'en' ? 'Monthly Rent (Per Unit)' : 'Renta Mensual (Por Unidad)'}
                        </label>
                        {userRentInputs.map((rent, index) => (
                          <div key={index} className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-slate-500 w-14">
                              {lang === 'en' ? `Unit ${index + 1}:` : `Unidad ${index + 1}:`}
                            </span>
                            <div className="relative flex-1">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <FormattedCurrencyInput
                                value={rent}
                                onChange={(val) => {
                                  const newInputs = [...userRentInputs];
                                  // store the PARSED number: consumers parseFloat() this, and
                                  // parseFloat("1,400") would be 1.
                                  newInputs[index] = parseFormattedNumber(val) ?? '';
                                  setUserRentInputs(newInputs);
                                }}
                                prefix={null}
                                placeholder="0"
                                className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                data-testid={`new-purchase-rent-input-${index}`}
                              />
                            </div>
                            <span className="text-xs text-slate-400">/mo</span>
                          </div>
                        ))}
                        <div className="text-sm font-semibold text-blue-700 mt-2">
                          {lang === 'en' ? 'Total:' : 'Total:'} 
                          ${userRentInputs.reduce((s, r) => s + (parseFloat(r) || 0), 0).toLocaleString()}/mo
                        </div>
                      </div>

                      {/* Closing Costs */}
                      {rentalFinancingType === 'dscr' && (
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <label className="text-sm font-semibold text-slate-700 block mb-2">
                            {lang === 'en' ? 'Closing Costs' : 'Costos de Cierre'}
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">
                                {lang === 'en' ? 'Base Closing %' : 'Cierre Base %'}
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={rentalClosingCostsPct}
                                  onChange={(e) => setRentalClosingCostsPct(parseFloat(e.target.value))}
                                  step="0.5"
                                  min="0"
                                  className="w-full pr-8 pl-3 py-1.5 border border-slate-200 rounded text-sm"
                                  data-testid="new-purchase-closing-costs-pct"
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">
                                {lang === 'en' ? 'Origination Points %' : 'Puntos Originación %'}
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={rentalOriginationPoints}
                                  onChange={(e) => setRentalOriginationPoints(parseFloat(e.target.value))}
                                  step="0.5"
                                  min="0"
                                  className="w-full pr-8 pl-3 py-1.5 border border-slate-200 rounded text-sm"
                                  data-testid="new-purchase-origination-points"
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">
                                {lang === 'en' ? 'Lender Fees $' : 'Costos Prestamista $'}
                              </label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                <FormattedCurrencyInput
                                  value={rentalLenderFees}
                                  onChange={(val) => setRentalLenderFees(parseFormattedNumber(val) ?? '')}
                                  prefix={null}
                                  placeholder="0"
                                  className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                  data-testid="new-purchase-lender-fees"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">
                                {lang === 'en' ? 'Title/Attorney $' : 'Título/Abogado $'}
                              </label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                <FormattedCurrencyInput
                                  value={rentalTitleAttorneyFees}
                                  onChange={(val) => setRentalTitleAttorneyFees(parseFormattedNumber(val) ?? '')}
                                  prefix={null}
                                  placeholder="0"
                                  className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                  data-testid="new-purchase-title-attorney"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Annual Expenses */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <label className="text-sm font-semibold text-slate-700 block mb-2">
                          {lang === 'en' ? 'Annual Expenses' : 'Gastos Anuales'}
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Property Taxes /yr' : 'Impuestos /año'}
                            </label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <FormattedCurrencyInput
                                value={annualPropertyTaxes}
                                onChange={(val) => setAnnualPropertyTaxes(parseFormattedNumber(val) ?? '')}
                                prefix={null}
                                placeholder="1,800"
                                className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                data-testid="new-purchase-annual-taxes"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">
                              {lang === 'en' ? 'Insurance /yr' : 'Seguro /año'}
                            </label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <FormattedCurrencyInput
                                value={annualInsurance}
                                onChange={(val) => setAnnualInsurance(parseFormattedNumber(val) ?? '')}
                                prefix={null}
                                placeholder="1,600"
                                className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                                data-testid="new-purchase-annual-insurance"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  )}

                  {/* INVESTMENT CRITERIA - Both paths */}
                  <div className="p-4 bg-white rounded-xl border border-slate-200">
                    <label className="text-sm font-semibold text-slate-700 block mb-3">
                      {lang === 'en' ? 'Investment Criteria' : 'Criterios de Inversión'}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">
                          {lang === 'en' ? 'Min Cash Flow /mo' : 'Flujo Mín /mes'}
                        </label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                          <FormattedCurrencyInput
                            value={rentalMinCashFlow}
                            onChange={(val) => setRentalMinCashFlow(parseFormattedNumber(val) || 0)}
                            prefix={null}
                            className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded text-sm"
                            data-testid="rental-min-cashflow"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Min DSCR</label>
                        <input
                          type="number"
                          value={rentalMinDscr}
                          onChange={(e) => setRentalMinDscr(parseFloat(e.target.value) || 1.25)}
                          step="0.05"
                          min="1"
                          className="w-full px-3 py-1.5 border border-slate-200 rounded text-sm"
                          data-testid="rental-min-dscr"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">
                          {lang === 'en' ? 'Min Cap Rate %' : 'Cap Rate Mín %'}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={rentalMinCapRate}
                            onChange={(e) => setRentalMinCapRate(parseFloat(e.target.value) || 6)}
                            step="0.5"
                            className="w-full pr-8 pl-3 py-1.5 border border-slate-200 rounded text-sm"
                            data-testid="rental-min-cap-rate"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">
                          {lang === 'en' ? 'Tolerance' : 'Tolerancia'}
                        </label>
                        <select
                          value={rentalToleranceMode}
                          onChange={(e) => setRentalToleranceMode(e.target.value)}
                          className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                          data-testid="rental-tolerance-mode"
                        >
                          <option value="strict">{lang === 'en' ? 'Strict' : 'Estricto'}</option>
                          <option value="balanced">{lang === 'en' ? 'Balanced' : 'Equilibrado'}</option>
                          <option value="aggressive">{lang === 'en' ? 'Aggressive' : 'Agresivo'}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* Analyze Button */}
              <button
                onClick={handleAnalyze}
                disabled={loading || !inputValue.trim() || (strategy === 'rental' && totalUserRent <= 0)}
                className="w-full px-6 py-3 sm:py-4 bg-secondary text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 text-base sm:text-lg touch-manipulation active:scale-[0.98]"
                data-testid="analyze-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('msg_analyzing')}
                  </>
                ) : strategy === 'rental' && totalUserRent <= 0 ? (
                  lang === 'en' ? 'Enter Rent to Analyze' : 'Ingrese Renta para Analizar'
                ) : (
                  t('input_analyze_btn')
                )}
              </button>
            </div>
          </div>

          {/* Sample Deal Analysis Section */}
          <div className="mt-8 sm:mt-10">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-primary mb-2">
                {lang === 'en' ? 'See a Sample Deal Analysis' : 'Ver Análisis de Ejemplo'}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {lang === 'en' 
                  ? 'Explore how CHEKSTE analyzes real investment properties. Click one of the examples below to see a full deal analysis.'
                  : 'Descubre cómo CHEKSTE analiza propiedades de inversión reales. Haz clic en uno de los ejemplos para ver un análisis completo.'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Fix & Flip Sample */}
              <button 
                type="button"
                className="group bg-white rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer text-left w-full touch-manipulation"
                onClick={(e) => {
                  e.preventDefault();
                  if (!loadingSampleDeal) handleSampleDealAnalysis('flip');
                }}
                disabled={!!loadingSampleDeal}
                data-testid="sample-flip-deal"
              >
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Hammer className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">
                        {lang === 'en' ? 'Sample Fix & Flip Deal' : 'Ejemplo de Flip'}
                      </h4>
                      <span className="text-xs text-amber-600 font-medium uppercase tracking-wide">
                        {lang === 'en' ? 'Fix & Flip' : 'Comprar y Renovar'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">553 Belmont St</span>
                    <span className="text-sm text-slate-500">• Detroit, MI</span>
                  </div>
                  
                  <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                    {lang === 'en' 
                      ? 'Example of a property with comparable sales used to estimate ARV and flip potential.'
                      : 'Ejemplo de propiedad con ventas comparables para estimar ARV y potencial de flip.'}
                  </p>
                  
                  <div 
                    className="w-full py-2.5 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all flex items-center justify-center gap-2 group-hover:shadow-md disabled:opacity-70"
                  >
                    {loadingSampleDeal === 'flip' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {lang === 'en' ? 'Analyzing...' : 'Analizando...'}
                      </>
                    ) : (
                      <>
                        {lang === 'en' ? 'Analyze This Sample' : 'Analizar Ejemplo'}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                </div>
              </button>
              
              {/* Rental Sample */}
              <button 
                type="button"
                className="group bg-white rounded-xl border-2 border-slate-200 hover:border-green-400 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer text-left w-full touch-manipulation"
                onClick={(e) => {
                  e.preventDefault();
                  if (!loadingSampleDeal) handleSampleDealAnalysis('rental');
                }}
                disabled={!!loadingSampleDeal}
                data-testid="sample-rental-deal"
              >
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">
                        {lang === 'en' ? 'Sample Rental Investment' : 'Ejemplo de Renta'}
                      </h4>
                      <span className="text-xs text-green-600 font-medium uppercase tracking-wide">
                        {lang === 'en' ? 'Rental Property' : 'Propiedad de Renta'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">227 Marlborough St</span>
                    <span className="text-sm text-slate-500">• Detroit, MI</span>
                  </div>
                  
                  <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                    {lang === 'en' 
                      ? 'Example rental property showing income analysis, estimated rent, and long-term investment metrics.'
                      : 'Ejemplo de propiedad de renta mostrando análisis de ingresos y métricas de inversión.'}
                  </p>
                  
                  <div 
                    className="w-full py-2.5 px-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all flex items-center justify-center gap-2 group-hover:shadow-md disabled:opacity-70"
                  >
                    {loadingSampleDeal === 'rental' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {lang === 'en' ? 'Analyzing...' : 'Analizando...'}
                      </>
                    ) : (
                      <>
                        {lang === 'en' ? 'Analyze This Sample' : 'Analizar Ejemplo'}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* How it affects analysis */}
          <div className="mt-8 bg-slate-50 rounded-xl p-6 border border-border">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-secondary" />
              {lang === 'en' ? 'How Asking Price Affects Your Analysis' : 'Cómo Afecta el Precio de Venta'}
            </h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-secondary font-bold">•</span>
                <span>
                  <strong>{lang === 'en' ? 'Net Profit:' : 'Ganancia Neta:'}</strong> {lang === 'en' ? 'Higher asking price reduces potential flip profit' : 'Un precio más alto reduce la ganancia potencial'}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary font-bold">•</span>
                <span>
                  <strong>{lang === 'en' ? 'MAO Comparison:' : 'Comparación MAO:'}</strong> {lang === 'en' ? 'Compares asking price vs. your Target Offer Price based on your selected criteria' : 'Compara el precio vs. tu Precio Objetivo basado en tus criterios seleccionados'}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary font-bold">•</span>
                <span>
                  <strong>{lang === 'en' ? 'Deal Score:' : 'Puntuación:'}</strong> {lang === 'en' ? 'Score is based on profit margins relative to asking price' : 'La puntuación se basa en márgenes de ganancia relativos al precio'}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Analyzer;
